import * as React from 'react';

import { DateTime } from 'luxon';

import { PhoneFormControl } from '@admin/components/fields/phone_form_control';
import { SelectWithOther } from '@admin/components/helpers/select_with_other';
import { LaborExceptionInput } from '@admin/components/labor_exception_input';
import { OrderAvailability } from '@admin/components/order_availability/order_availability';
import { MoversSharedFields } from '@admin/components/orders/movers_shared_fields';
import { MovingSubtypeFormGroup } from '@admin/components/orders/moving_subtype_form_group';
import { PredecessorOrderSelector } from '@admin/components/orders/predecessor_order_selector';
import { RegionFormGroup, RegionFragment } from '@admin/components/orders/region_form_group';
import { ORDER_SUBTYPE_MAP } from '@admin/utils/order_type_map';

import { DateTimeWithTimezone } from './date_time_with_timezone';

declare const window: {
  clutter: {
    user: {
      id: string;
      roles: string[];
    };
  };
};

const MIN_SCHEDULED = DateTime.now().minus({ days: 4 }).toFormat('yyyy-MM-dd');
const MAX_SCHEDULED = DateTime.now().plus({ years: 2 }).toFormat('yyyy-MM-dd');

const MOVE_RESCHEDULE_FEE_DESCRIPTION =
  'A rescheduling fee of $100 will be charged for rescheduling within 48 hours of the appointment. ' +
  'Your initial deposit will be applied towards the new order.';

const SLA_WINDOW_OPTIONS = [
  '1 Hour SLA',
  '2 Hour SLA',
  '3 Hour SLA',
  '4 Hour SLA',
  '5 Hour SLA',
  '6 Hour SLA',
  '7 Hour SLA',
  '8 Hour SLA',
];

const SCHEDULE_OVERRIDE_REASONS = [
  'Customer: Underquote',
  'Customer: Building Restrictions Not Provided',
  'Customer: Elevator Not Reserved',
  'Customer: No Day of Contact',
  'Customer: Reinstating Appointment',
  'Customer: Other',
  'Clutter: Staffing',
  'Clutter: Day Of Callouts',
  'Clutter: Street Closures',
  'Clutter: Truck Issue',
  'Clutter: Material Issue',
  'Clutter: Other',
];

const FOLLOW_UP_EXCEPTION_REASONS = [
  'Clutter Late Arrival',
  'Slower Than Expected Throughput',
  'Not Enough Truck Space',
];

interface IAddress {
  schedulable?: boolean;
  state?: string;
}

interface ILaborException {
  reason: string;
}

interface IContact {
  name?: string;
  phone?: string;
}

interface IMovingOperation {
  moving_configuration_id?: string;
  origin_address?: { state?: string };
  destination_address?: { state?: string };
}

interface ISubscription {
  id: string;
  quantity?: number;
  plan_id?: number;
  plan?: {
    id: number;
    type?: string;
  };
  custom_plan?: boolean;
  dimensions?: {
    length?: string;
    width?: string;
  };
}

interface IOrder {
  id?: number;
  type: string;
  subtype?: string;
  region?: RegionFragment;
  address?: IAddress;
  contact?: IContact;
  other?: string;
  labor_exception?: ILaborException;
  predecessor_id?: string | number | null;
  follow_up_job_exception?: boolean;
  follow_up_job_exception_reason?: string | null;
  scheduled?: Date;
  scheduled_override?: boolean;
  scheduled_override_reason?: string;
  sla_window_size?: number;
  pickup_deadline?: Date;
  rescheduled_at?: Date;
  rescheduler?: { id: string; type: string };
  rescheduling?: boolean;
  cardless?: boolean;
  state?: string;
  external?: boolean;
  moving?: boolean;
  moving_operation?: IMovingOperation;
  movers?: number;
  override_movers?: boolean;
  movers_overwritten_at?: Date | null;
  mover_override_reason?: string;
  reonboarding?: boolean;
  account_id?: number;
}

interface IAccount {
  id: string;
  customer: {
    id: string;
    name: string;
  };
  subscriptions?: ISubscription[];
}

interface IOrderSharedFieldsProps {
  order: IOrder;
  account: IAccount;
  onboardingDate?: string;
  warehouseReturn?: boolean;
  /** External movers value passed from Angular for syncing state */
  externalMovers?: number;
  /** External region value passed from Angular for syncing state (e.g., after geocode) */
  externalRegion?: RegionFragment;
  onOrderChange?: () => void;
}

/**
 * Shared fields component for order forms.
 *
 * @param props.order - The order object
 * @param props.account - The account object
 * @param props.onboardingDate - The onboarding date for follow-up jobs
 * @param props.warehouseReturn - Whether this is a warehouse return order
 * @param props.externalMovers - External movers value for syncing with Angular
 * @param props.externalRegion - External region value for syncing with Angular (e.g., after geocode)
 */
export const OrderSharedFields: React.FC<IOrderSharedFieldsProps> = ({
  order,
  account,
  onboardingDate,
  warehouseReturn,
  externalMovers,
  externalRegion,
  onOrderChange,
}) => {
  const userRoles = window.clutter?.user?.roles || [];
  const userId = window.clutter?.user?.id;

  const notifyOrderChange = React.useCallback(() => {
    onOrderChange?.();
  }, [onOrderChange]);

  // React state for mutable fields that affect UI rendering
  const [overrideMovers, setOverrideMovers] = React.useState(order.override_movers || false);
  const [movers, setMovers] = React.useState(order.movers);
  const [moverOverrideReason, setMoverOverrideReason] = React.useState(order.mover_override_reason);
  const [contactName, setContactName] = React.useState(order.contact?.name || '');
  const [contactPhone, setContactPhone] = React.useState(order.contact?.phone || '');
  const [otherNotes, setOtherNotes] = React.useState(order.other || '');
  const [scheduledOverride, setScheduledOverride] = React.useState(order.scheduled_override || false);
  const [rescheduler, setRescheduler] = React.useState(order.rescheduler);
  const [rescheduling, setRescheduling] = React.useState(order.rescheduling || false);
  const [followUpJobException, setFollowUpJobException] = React.useState(order.follow_up_job_exception || false);
  const [subtype, setSubtype] = React.useState(order.subtype);
  const [region, setRegion] = React.useState(order.region);
  const [laborException, setLaborException] = React.useState(order.labor_exception);
  const [predecessorId, setPredecessorId] = React.useState(order.predecessor_id);
  const [followUpJobExceptionReason, setFollowUpJobExceptionReason] = React.useState(
    order.follow_up_job_exception_reason,
  );
  const [scheduled, setScheduled] = React.useState(order.scheduled);
  const [pickupDeadline, setPickupDeadline] = React.useState(order.pickup_deadline);
  const [scheduledOverrideReason, setScheduledOverrideReason] = React.useState(order.scheduled_override_reason);
  const [slaWindowSize, setSlaWindowSize] = React.useState(order.sla_window_size);

  // Sync state with external changes from parent order form state (e.g., geocode or return mover estimation)
  // We use external* props since React can't detect mutations on the same object reference
  React.useEffect(() => {
    if (externalMovers !== undefined) {
      setMovers(externalMovers);
    }
  }, [externalMovers]);

  React.useEffect(() => {
    if (externalRegion !== undefined) {
      setRegion(externalRegion);
    }
  }, [externalRegion]);

  React.useEffect(() => {
    setOverrideMovers(order.override_movers || false);
  }, [order.override_movers]);

  React.useEffect(() => {
    setScheduledOverride(order.scheduled_override || false);
  }, [order.scheduled_override]);

  React.useEffect(() => {
    setRescheduler(order.rescheduler);
  }, [order.rescheduler]);

  const hasRole = React.useCallback(
    (...roles: string[]) => roles.some((role) => userRoles.includes(role)),
    [userRoles],
  );

  const hasScheduleOverridePermission = React.useMemo(() => hasRole('admin', 'dispatcher', 'manager'), [hasRole]);

  const showLaborException = React.useMemo(
    () =>
      (subtype !== 'onboarding' || order.reonboarding) &&
      order.type !== 'selfstorage-shuttle' &&
      order.type !== 'retail-delivery' &&
      hasRole('admin', 'billing', 'care', 'dispatcher'),
    [subtype, order.reonboarding, order.type, hasRole],
  );

  const showPredecessor = React.useMemo(
    () => order.type !== 'selfstorage-shuttle' && order.type !== 'retail-delivery' && hasRole('admin', 'dispatcher'),
    [order.type, hasRole],
  );

  const schedulable = React.useMemo(
    () => order.external && !warehouseReturn && (!order.address || order.address.schedulable),
    [order.external, warehouseReturn, order.address],
  );

  // Called by OrderAvailability after it mutates order.scheduled and order.scheduled_override
  // We sync those mutations to React state to trigger re-renders
  const reschedule = React.useCallback(() => {
    const isRescheduling = !!(order.id && order.state !== 'pending' && schedulable);
    order.rescheduling = isRescheduling;
    setRescheduling(isRescheduling);
    setScheduledOverride(order.scheduled_override || false);
    setScheduled(order.scheduled);
    notifyOrderChange();
  }, [order, schedulable, notifyOrderChange]);

  const showMovingReschedulingWarning = React.useMemo(() => {
    if (!onboardingDate) return false;
    return DateTime.now().toMillis() > DateTime.fromISO(onboardingDate).minus({ days: 2 }).toMillis();
  }, [onboardingDate]);

  // Handlers
  const onSelectSubtype = React.useCallback(
    (newSubtype: keyof typeof ORDER_SUBTYPE_MAP) => {
      order.subtype = newSubtype;
      setSubtype(newSubtype);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onSelectRegion = React.useCallback(
    (newRegion?: RegionFragment) => {
      order.region = newRegion;
      setRegion(newRegion);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onOverrideMovers = React.useCallback(
    (value: boolean) => {
      order.override_movers = value;
      setOverrideMovers(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onMoversChange = React.useCallback(
    (value?: number) => {
      order.movers = value;
      setMovers(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onMoverOverrideReasonChange = React.useCallback(
    (value?: string) => {
      order.mover_override_reason = value;
      setMoverOverrideReason(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const handleContactNameChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!order.contact) order.contact = {};
      // Title case the name
      const value = event.target.value
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      order.contact.name = value;
      setContactName(value);
    },
    [order],
  );

  const handleContactPhoneChange = React.useCallback(
    (value?: string) => {
      if (!order.contact) order.contact = {};
      order.contact.phone = value;
      setContactPhone(value || '');
    },
    [order],
  );

  const handleOtherChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      order.other = event.target.value;
      setOtherNotes(event.target.value);
    },
    [order],
  );

  const updateLaborException = React.useCallback(
    (checked: boolean) => {
      const newValue = checked ? { reason: '' } : undefined;
      order.labor_exception = newValue;
      setLaborException(newValue);
    },
    [order],
  );

  const updateExceptionReason = React.useCallback(
    (value: string) => {
      if (order.labor_exception) {
        order.labor_exception.reason = value;
        setLaborException({ ...order.labor_exception });
      }
    },
    [order],
  );

  const onSelectPredecessorID = React.useCallback(
    (newPredecessorID?: string | number) => {
      const value = newPredecessorID ?? null;
      order.predecessor_id = value;
      setPredecessorId(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onFollowUpJobException = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      order.follow_up_job_exception = checked;
      setFollowUpJobException(checked);
      if (!checked) {
        order.follow_up_job_exception_reason = null;
        setFollowUpJobExceptionReason(null);
      }
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onFollowUpJobExceptionReason = React.useCallback(
    (value: string) => {
      order.follow_up_job_exception_reason = value;
      setFollowUpJobExceptionReason(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onScheduledOverrideChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      order.scheduled_override = checked;
      setScheduledOverride(checked);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onScheduledChange = React.useCallback(
    (value: Date | undefined) => {
      order.scheduled = value;
      setScheduled(value);
      reschedule();
    },
    [order, reschedule],
  );

  const onPickupDeadlineChange = React.useCallback(
    (value: Date | undefined) => {
      order.pickup_deadline = value;
      setPickupDeadline(value);
    },
    [order],
  );

  const onSLAWindowSizeOverride = React.useCallback(
    (value: string) => {
      // Parse "X Hour SLA" to get the number, then multiply by 2 for 30-minute intervals
      const hours = parseInt(value.slice(0, 1), 10);
      const newValue = hours * 2;
      order.sla_window_size = newValue;
      setSlaWindowSize(newValue);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const slaWindowSizeText = React.useMemo(() => {
    if (!slaWindowSize) return '';
    const hours = slaWindowSize / 2;
    return `${hours} Hour SLA`;
  }, [slaWindowSize]);

  const onScheduledOverrideReasonChange = React.useCallback(
    (value: string) => {
      order.scheduled_override_reason = value;
      setScheduledOverrideReason(value);
      notifyOrderChange();
    },
    [order, notifyOrderChange],
  );

  const onReschedulerChange = React.useCallback(
    (type: 'Customer' | 'User') => {
      const newRescheduler =
        type === 'Customer'
          ? { id: account.customer.id, type: 'Customer' as const }
          : { id: userId, type: 'User' as const };
      order.rescheduler = newRescheduler;
      setRescheduler(newRescheduler);
      notifyOrderChange();
    },
    [order, account.customer.id, userId, notifyOrderChange],
  );

  const timezone = region?.tz || 'UTC';

  // Build order object compatible with OrderAvailability
  // The OrderAvailability component has its own IOrder interface that expects region.id as number
  // IMPORTANT: We must keep the same object reference so mutations propagate back to Angular
  // Only override the region.id type conversion, keeping the original order object
  const orderForAvailability = React.useMemo(() => {
    // Mutate the region.id in place if needed for type compatibility, but keep same order reference
    const orderWithTypedRegion = order as typeof order & {
      region?: { id: number; tz?: string };
    };
    return orderWithTypedRegion;
  }, [order]);

  // Build subscriptions array compatible with OrderAvailability
  const subscriptionsForAvailability = React.useMemo(
    () =>
      (account.subscriptions || []).map((sub) => ({
        ...sub,
        quantity: (sub as { quantity?: number }).quantity || 1,
      })),
    [account.subscriptions],
  );

  return (
    <fieldset className="form-horizontal">
      <h3 className="text-thin">Order Details</h3>
      <div className="row">
        <div className="col-sm-8">
          {order.type === 'moving-move' && subtype && (
            <MovingSubtypeFormGroup selection={subtype as keyof typeof ORDER_SUBTYPE_MAP} onSelect={onSelectSubtype} />
          )}

          {(order.type !== 'selfstorage-shuttle' || order.id) && (
            <RegionFormGroup selection={region} onSelect={onSelectRegion} />
          )}

          <MoversSharedFields
            orderID={order.id ? String(order.id) : undefined}
            orderType={order.type}
            overrideMovers={overrideMovers}
            onOverrideMovers={onOverrideMovers}
            movers={movers}
            onMovers={onMoversChange}
            moverOverrideReason={moverOverrideReason}
            onMoverOverrideReason={onMoverOverrideReasonChange}
            movingConfigurationID={order.moving_operation?.moving_configuration_id}
          />

          <div className="form-group">
            <label htmlFor="contact_name" className="control-label col-sm-2">
              Day-of Contact
            </label>
            <div className="col-sm-5">
              <input
                id="contact_name"
                name="order[contact][name]"
                type="text"
                placeholder="Name"
                className="form-control"
                value={contactName}
                onChange={handleContactNameChange}
              />
            </div>
            <div className="col-sm-5">
              <PhoneFormControl
                id="contact_phone"
                name="order[contact][phone]"
                value={contactPhone}
                onChange={handleContactPhoneChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="order_other" className="control-label col-sm-2">
              Other
            </label>
            <div className="col-sm-10">
              <textarea
                id="order_other"
                name="order[other]"
                className="form-control"
                placeholder="Anything else that is important for the order (large guard dog is actually harmless)."
                value={otherNotes}
                onChange={handleOtherChange}
              />
            </div>
          </div>

          {showLaborException && (
            <div className="form-group">
              <LaborExceptionInput
                laborException={laborException}
                onCheck={updateLaborException}
                onSelect={updateExceptionReason}
              />
            </div>
          )}

          {showPredecessor && (
            <div className="form-group">
              <label htmlFor="order_follow_up_job" className="control-label col-sm-2">
                Predecessor
              </label>
              <div className="col-sm-4">
                <PredecessorOrderSelector
                  accountID={account.id}
                  orderType={order.type}
                  orderID={order.id}
                  predecessorID={predecessorId ?? ''}
                  onSelectPredecessorID={onSelectPredecessorID}
                />
              </div>
              <label htmlFor="follow_up_job_exception" className="control-label col-sm-2">
                Waive Follow-Up Fee?
              </label>
              <div className="col-sm-1 col-auto">
                <input
                  type="checkbox"
                  id="follow_up_job_exception"
                  checked={followUpJobException}
                  onChange={onFollowUpJobException}
                  disabled={!predecessorId}
                />
              </div>
              <div className="col-sm-3">
                <SelectWithOther
                  option={followUpJobExceptionReason || undefined}
                  onSelect={onFollowUpJobExceptionReason}
                  options={FOLLOW_UP_EXCEPTION_REASONS}
                  disabled={!followUpJobException}
                  placeholder="-- Reason --"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <hr />

      <div>
        <h3 className="text-thin">Schedule</h3>
        <div className="row">
          <div className="col-sm-12">
            {schedulable && (
              <div className="schedule-container">
                {onboardingDate && order.type !== 'moving-move' && (
                  <p className="text-bold text-danger">Schedule after {onboardingDate}.</p>
                )}
                {order.type === 'moving-move' && (
                  <p className="text-bold text-danger">
                    Changing the date can affect labor rates and minimums. Please check the moving quote above after
                    selecting a date.
                  </p>
                )}

                <div className="form-group">
                  <div className="col-sm-12">
                    <OrderAvailability
                      order={orderForAvailability as Parameters<typeof OrderAvailability>[0]['order']}
                      timezone={timezone}
                      subscriptions={
                        subscriptionsForAvailability as Parameters<typeof OrderAvailability>[0]['subscriptions']
                      }
                      onScheduleChange={reschedule}
                    />
                  </div>
                </div>

                <div className="form-group">
                  {!hasScheduleOverridePermission && <div className="col-sm-1"></div>}
                  {hasScheduleOverridePermission && (
                    <label htmlFor="order_scheduled" className="control-label col-sm-1">
                      Schedule Override
                    </label>
                  )}
                  <div className="col-sm-11">
                    {hasScheduleOverridePermission && (
                      <div className="form-inline">
                        <div className="checkbox pad-rgt">
                          <input
                            type="checkbox"
                            id="order_scheduled_override"
                            checked={scheduledOverride}
                            onChange={onScheduledOverrideChange}
                          />
                        </div>
                        <DateTimeWithTimezone
                          id="order_scheduled"
                          value={scheduled}
                          onChange={onScheduledChange}
                          timezone={timezone}
                          min={MIN_SCHEDULED}
                          max={MAX_SCHEDULED}
                          disabled={!scheduledOverride}
                        />
                        <SelectWithOther
                          option={slaWindowSizeText}
                          onSelect={onSLAWindowSizeOverride}
                          options={SLA_WINDOW_OPTIONS}
                          disabled={!scheduledOverride}
                          placeholder="Select SLA Window Size"
                          hideOther={true}
                        />
                        <SelectWithOther
                          option={scheduledOverrideReason}
                          onSelect={onScheduledOverrideReasonChange}
                          options={SCHEDULE_OVERRIDE_REASONS}
                          disabled={!scheduledOverride}
                          placeholder="Select Reason for Schedule Override"
                          hideOther={true}
                        />
                      </div>
                    )}

                    {order.type === 'moving-move' && showMovingReschedulingWarning && (
                      <div className="text-danger col-sm-6">
                        <p>If this order is rescheduled, please inform the customer that:</p>
                        &quot;{MOVE_RESCHEDULE_FEE_DESCRIPTION}&quot;
                      </div>
                    )}

                    {order.type !== 'moving-move' && (
                      <>
                        {order.rescheduled_at && !order.cardless && (
                          <div className="text-danger">
                            This order has already been rescheduled by the customer. If this order is rescheduled again
                            or cancelled, customer will be charged a reschedule fee.
                          </div>
                        )}
                        {(!order.rescheduled_at || order.cardless) && (
                          <div className="text-success">Reschedule fee will be waived for this order.</div>
                        )}
                      </>
                    )}

                    {rescheduling && (
                      <div>
                        <label className="control-label">
                          Who requested the reschedule? <span className="text-danger">(Must Select One)</span>
                        </label>
                        <div>
                          <label className="radio-inline">
                            <input
                              type="radio"
                              name="order[rescheduler]"
                              id="order-rescheduler-customer"
                              checked={rescheduler?.type === 'Customer'}
                              onChange={() => onReschedulerChange('Customer')}
                            />
                            Customer - {account.customer.name}
                          </label>
                          <label className="radio-inline">
                            <input
                              type="radio"
                              name="order[rescheduler]"
                              id="order-rescheduler-user"
                              checked={rescheduler?.type === 'User'}
                              onChange={() => onReschedulerChange('User')}
                            />
                            Clutter Employee
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!schedulable && (
              <div className="schedule-fallback">
                <div className="form-group flex center">
                  <label htmlFor="order_scheduled_maintenance" className="control-label col-sm-1">
                    Order Scheduled
                  </label>
                  <div className="col-sm-11">
                    <DateTimeWithTimezone
                      value={scheduled}
                      onChange={onScheduledChange}
                      timezone={timezone}
                      min={MIN_SCHEDULED}
                      max={MAX_SCHEDULED}
                    />
                  </div>
                </div>
              </div>
            )}

            {order.type === 'pickup' && (
              <div className="form-group flex center">
                <label htmlFor="order_pickup_deadline" className="control-label col-sm-1">
                  Deadline (optional)
                </label>
                <div className="col-sm-11">
                  <DateTimeWithTimezone
                    value={pickupDeadline}
                    onChange={onPickupDeadlineChange}
                    timezone={timezone}
                    min={MIN_SCHEDULED}
                    max={MAX_SCHEDULED}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </fieldset>
  );
};
